// Funzione di merge semplificata per incontri individuali
// Mantiene i risultati già disputati quando si aggiunge/cambia/rimuove un atleta nei gironi

/**
 * Esegue il merge tra vecchi e nuovi incontri, mantenendo i risultati dove possibile
 * usando un identificatore permanente (permanentId) basato sui nomi dei tiratori.
 * 
 * Questo approccio semplificato utilizza solo i nomi dei tiratori come identificatori stabili,
 * indipendentemente dagli indici che possono cambiare quando un tiratore viene rimosso.
 *
 * @param {Array} oldMatches - Vecchia lista incontri (con risultati)
 * @param {Array} newMatches - Nuova lista incontri (solo struttura)
 * @param {Object} currentTeams - Dizionario dei team/atleti attuali
 * @returns {Array} - Lista degli incontri con risultati preservati dove possibile
 */
export function mergeIndividualMatches(oldMatches, newMatches, currentTeams) {
  console.log('[DATA-FLOW] mergeIndividualMatches - START');
  console.log('----- MERGE INDIVIDUAL MATCHES -----');
  
  // Safety check per variabili di input
  if (!newMatches) return [];
  if (!oldMatches || oldMatches.length === 0) return newMatches;
  
  try {
    // Ottieni i teams vecchi dalla struttura dei match vecchi
    const oldTeams = {};
    
    // Estrapola i team vecchi dai match
    oldMatches.forEach(match => {
      if (!match) return; // Safety check
      
      // Se i team hanno campo members (0..n elementi), li manteniamo
      if (match.teamA !== undefined && match._teamA?.members) {
        oldTeams[match.teamA] = match._teamA;
      }
      
      if (match.teamB !== undefined && match._teamB?.members) {
        oldTeams[match.teamB] = match._teamB;
      }
    });
    
    // Debug - controlla che gli ID stabili siano stati assegnati correttamente
    console.log('DEBUG: Old Teams Map:', Object.keys(oldTeams).length);
    
    // Creiamo una mappa dei vecchi match usando permanentId come chiave
    const oldMatchesMap = {};
    if (oldMatches && oldMatches.length > 0) {
      oldMatches.forEach(match => {
        if (!match) return; // Safety check
        
        try {
          // Otteniamo i nomi dei tiratori dal match vecchio
          const oldTeamAName = oldTeams[match.teamA]?.name || 
                               oldTeams[match.teamA]?.members?.[0] || 
                               `old-${match.teamA}`;
          
          const oldTeamBName = oldTeams[match.teamB]?.name || 
                               oldTeams[match.teamB]?.members?.[0] || 
                               `old-${match.teamB}`;
          
          // Se il match ha già un permanentId, usiamo quello
          let permanentKey = match.permanentId;
          
          // Altrimenti, creiamo una chiave stabile basata sui nomi dei tiratori
          if (!permanentKey) {
            // Ordiniamo alfabeticamente i nomi per avere una chiave stabile 
            // indipendente dall'ordine
            permanentKey = `match_${[oldTeamAName, oldTeamBName].sort().join('_vs_')}`;
          }
          
          // Salviamo il match nel dizionario
          oldMatchesMap[permanentKey] = {
            match,
            teamAName: oldTeamAName,
            teamBName: oldTeamBName
          };
          
          console.log(`Match vecchio: ${oldTeamAName} vs ${oldTeamBName} → PermanentID: ${permanentKey} ` + 
                     `(scoreA: ${match.scoreA}, scoreB: ${match.scoreB}, completed: ${match.completed})`);
        } catch (err) {
          console.error('Errore nell\'elaborazione del match vecchio:', err, match);
        }
      });
    }
    
    console.log('==== APPLICAZIONE RISULTATI ====');
    console.log('Vecchi match indicizzati:', Object.keys(oldMatchesMap).length);
    console.log('Nuovi match da elaborare:', newMatches ? newMatches.length : 0);
    
    // Applica il merge preservando i risultati esistenti
    const mergedMatches = newMatches.map(newMatch => {
      try {
        // Estrai i nomi dei tiratori del nuovo match
        const newTeamAName = currentTeams[newMatch.teamA] ? 
            (currentTeams[newMatch.teamA].name || currentTeams[newMatch.teamA].members?.[0] || newMatch.teamA) : 
            newMatch.teamA;
            
        const newTeamBName = currentTeams[newMatch.teamB] ? 
            (currentTeams[newMatch.teamB].name || currentTeams[newMatch.teamB].members?.[0] || newMatch.teamB) : 
            newMatch.teamB;
        
        // Assicuriamoci che tiratorA e tiratorB esistano sempre
        if (!newMatch.tiratorA) newMatch.tiratorA = newTeamAName;
        if (!newMatch.tiratorB) newMatch.tiratorB = newTeamBName;
        
        // Se il match ha già un permanentId, lo usiamo come è
        // Altrimenti lo creiamo nello stesso modo fatto per i vecchi match
        if (!newMatch.permanentId) {
          newMatch.permanentId = `match_${[newTeamAName, newTeamBName].sort().join('_vs_')}`;
        }
        
        console.log(`DEBUG: Nuovo match - ${newTeamAName} vs ${newTeamBName} - PermanentID: ${newMatch.permanentId}`);
        
        // Cerchiamo corrispondenze usando il permanentId come chiave principale
        const oldMatchData = oldMatchesMap[newMatch.permanentId];
        
        // Se abbiamo trovato un match vecchio con lo stesso permanentId
        if (oldMatchData) {
          const oldMatch = oldMatchData.match;
          
          // Determiniamo se il match è invertito rispetto al vecchio
          // confrontando i nomi dei tiratori
          const isInverted = 
              (newTeamAName === oldMatchData.teamBName && 
               newTeamBName === oldMatchData.teamAName);
          
          console.log(`MATCH: Trovata corrispondenza per ${newMatch.permanentId} ${isInverted ? '(invertito)' : ''}`);
          console.log(`  Vecchio match: ${oldMatchData.teamAName} vs ${oldMatchData.teamBName} = ${oldMatch.scoreA}-${oldMatch.scoreB}`);
          console.log(`  Nuovo match: ${newTeamAName} vs ${newTeamBName}`);
          
          // Copia i risultati dal vecchio match al nuovo match
          const newMergedMatch = { ...newMatch };
          
          // Se il match è invertito, scambiamo i punteggi
          if (isInverted) {
            newMergedMatch.scoreA = oldMatch.scoreB;
            newMergedMatch.scoreB = oldMatch.scoreA;
          } else {
            newMergedMatch.scoreA = oldMatch.scoreA;
            newMergedMatch.scoreB = oldMatch.scoreB;
          }
          
          // Copiamo gli altri attributi importanti
          newMergedMatch.completed = oldMatch.completed;
          if (oldMatch.noteResults) newMergedMatch.noteResults = oldMatch.noteResults;
          
          return newMergedMatch;
        }
        
        // Se non è stata trovata nessuna corrispondenza, restituisci il match originale
        return newMatch;
      } catch (err) {
        console.error('Errore nel merge del match:', err, newMatch);
        return newMatch;
      }
    });
    
    // Statistiche finali
    const preservedCount = mergedMatches.filter(m => m.completed || (m.scoreA !== undefined && m.scoreA !== null)).length;
    console.log(`==== MERGE COMPLETATO ====\nNuovi match totali: ${mergedMatches.length}\nMatch con risultati preservati: ${preservedCount}`);
    
    console.log('[DATA-FLOW] mergeIndividualMatches - END');
    return mergedMatches;
  } catch (err) {
    console.error('Errore generale nel merge dei match:', err);
    return newMatches;
  }
}
